import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import {
  claimBlobTab, showBlobInTab, saveBlobAs, POPUP_BLOCKED_MESSAGE,
  printsViaTab, printBlobInFrame, disposePrintFrame, PRINT_IN_TAB_MESSAGE, PRINT_FAILED_MESSAGE,
} from '../../lib/blob-tab';
import { BackHeader } from '../../components/BackHeader';
import { useListState } from '../../hooks/useListState';
import { useAuth } from '../../contexts/AuthContext';
import { MaintenanceFlagsPanel } from '../../components/equipment/MaintenanceFlagsPanel';
import { MAINTENANCE_FLAGS_ROOT } from '../../hooks/useMaintenanceFlags';
import { useLocale } from '../../contexts/LocaleContext';
import JobChatDrawer from '../../components/JobChatDrawer';
import { isSalesRole } from '../../lib/sales-access';
import FieldCasingStringCard from '../../components/casing/FieldCasingStringCard';
import { jobCasingStringsForDisplay } from '../../lib/casing-strings-display';
import { crewRoleLabel } from '../../lib/display-labels';
import { serviceNames } from '../../lib/service-entries';
import { getJobTypeLabel } from '../../constants/job-types';
// created_at / updated_at are `timestamp with timezone` in the API schema, so
// the DAY they fall on is the viewer's own and formatInstantDay is the right
// helper — NOT formatCalendarDate, which is already imported here for the job's
// calendar-day columns and is the easy wrong reach. @see calendar-date.ts.
import { formatCalendarDate, formatInstantDay, formatScheduledTime } from '../../lib/calendar-date';
import { PHASE_WORD, phaseDateValue, type FieldPhase } from '../../lib/job-phase-dates';
import { jobHasSeparateWashDay } from '../../lib/dispatch-phase-cards';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { phaseIsResumable, useResumePhaseMutation } from '../../hooks/useResumePhaseMutation';
import { JOB_STATUS_STYLES, JOB_STATUS_LABELS, type JobStatus } from '../../lib/job-status';
import {
  describeCallSheetSend,
  describeCallSheetFailure,
  type SendReport,
} from '../../lib/call-sheet-send-result';
import {
  Loader,
  MapPin,
  Users,
  Wrench,
  Calendar,
  Hash,
  Layers,
  FileText,
  Gauge,
  Receipt,
  Eye,
  ChevronDown,
  ChevronUp,
  Download,
  Printer,
  Mail,
  MessageSquare,
  Link,
  Link2,
  Navigation,
  AlertTriangle,
  CheckCircle2,
  Circle,
  Clock,
  Tag,
  Pencil,
  Phone,
  Play,
  Plus,
  Trash2,
  User,
  X,
  Paperclip,
  Upload,
  RotateCw,
} from 'lucide-react';

// ─── Status badge styles ────────────────────────────────────────────────────
// Both maps moved to lib/job-status.ts so the three field screens can share the
// office copy instead of hand-rolling a fourth. Values are unchanged; the call
// sites below read the same colours and labels they always did.

// ─── Equipment status styles ────────────────────────────────────────────────
const EQUIP_STATUS_STYLES: Record<string, string> = {
  available: 'text-green-400',
  reserved: 'text-purple-400',
  in_use: 'text-blue-400',
  left_on_site: 'text-orange-400',
  maintenance: 'text-red-400',
  retired: 'text-gray-400',
};

const EQUIP_STATUS_LABELS: Record<string, string> = {
  available: 'Available',
  reserved: 'Reserved',
  in_use: 'In Use',
  left_on_site: 'Left on Site',
  maintenance: 'Maintenance',
  retired: 'Retired',
};

// ─── Cert badge styles ──────────────────────────────────────────────────────
const CERT_STYLES: Record<string, { label: string; color: string }> = {
  H2S_alive: { label: 'H2S Alive', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  CSTS: { label: 'CSTS', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  standard_first_aid: { label: 'First Aid', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  ground_disturbance: { label: 'Ground Dist.', color: 'bg-purple-500/20 text-purple-400 border-purple-500/30' },
  WHMIS: { label: 'WHMIS', color: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
  OSHA_10: { label: 'OSHA 10', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  OSHA_30: { label: 'OSHA 30', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
  H2S_awareness: { label: 'H2S Awareness', color: 'bg-green-500/20 text-green-400 border-green-500/30' },
  first_aid_CPR: { label: 'First Aid/CPR', color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' },
  other: { label: 'Other', color: 'bg-gray-500/20 text-gray-400 border-gray-500/30' },
};

// ─── Service chip colors ────────────────────────────────────────────────────
const SERVICE_COLORS = [
  'bg-amber-500/15 text-amber-400 border-amber-500/30',
  'bg-blue-500/15 text-blue-400 border-blue-500/30',
  'bg-green-500/15 text-green-400 border-green-500/30',
  'bg-purple-500/15 text-purple-400 border-purple-500/30',
  'bg-cyan-500/15 text-cyan-400 border-cyan-500/30',
  'bg-rose-500/15 text-rose-400 border-rose-500/30',
];

// ─── Attachment helpers ─────────────────────────────────────────────────────
const ALLOWED_ATTACHMENT_TYPES = '.pdf,.jpg,.jpeg,.png,.csv,.doc,.docx,.xls,.xlsx';
const ALLOWED_MIME_SET = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);
const ALLOWED_EXT_SET = new Set(['pdf', 'jpg', 'jpeg', 'png', 'csv', 'doc', 'docx', 'xls', 'xlsx']);
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function validateAttachmentFile(file: File): string | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_EXT_SET.has(ext) && !ALLOWED_MIME_SET.has(file.type)) {
    return `File type ".${ext}" is not allowed. Allowed: PDF, JPEG, PNG, CSV, Word, Excel.`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `File is too large (${formatFileSize(file.size)}). Maximum size is 50 MB.`;
  }
  return null;
}

// ─── Country flag helper ────────────────────────────────────────────────────
const CountryFlag: React.FC<{ country?: string }> = ({ country }) => {
  if (country === 'CA') return <span className="text-xl" title="Canada">🇨🇦</span>;
  if (country === 'US') return <span className="text-xl" title="USA">🇺🇸</span>;
  return null;
};

const EMAIL_WORDING = { noun: 'emailed', verb: 'emails' } as const;
const SMS_WORDING = { noun: 'texted', verb: 'texts' } as const;
export const JobDetail: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  // Created / updated / uploaded are instants, shown as the TENANT's day.
  const timeZone = useTenantTimeZone();
  const location = useLocation();
  const navigate = useNavigate();
  const { restoreListState } = useListState();
  const { user } = useAuth();
  // A sales user reads the job and changes nothing (operator, 2026-10-11;
  // api#1032). Every write control below is not drawn for them, and the reads
  // the API refuses them — tasks, attachments, call sheet, chat — are neither
  // fired nor drawn. (The maintenance panel gates itself: its READ_ROLES
  // already exclude sales.) Every other role is unchanged.
  const isSales = isSalesRole(user?.role);
  const { toDisplayDepth, toDisplayPressure, formatDepth, formatPressure, wellIdLabel } = useLocale();
  const [callSheetOpen, setCallSheetOpen] = useState(false);
  // "Text to Crew" on a split job asks which phase before sending.
  const [smsPhaseAsk, setSmsPhaseAsk] = useState(false);
  const [directionsOpen, setDirectionsOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  // A job-chat push opens this screen with ?chat=1 (fieldiq-api job-chat-notify).
  const [chatOpen, setChatOpen] = useState(() => new URLSearchParams(window.location.search).get('chat') === '1');
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [deleteAttachmentId, setDeleteAttachmentId] = useState<string | null>(null);
  const [showAddLink, setShowAddLink] = useState(false);
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const [toast, setToast] = useState<
    { type: 'success' | 'warning' | 'error'; msg: string; detail?: string[] } | null
  >(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (location.state?.from?.includes('/jobs') || location.state?.from?.includes('/dispatch')) {
      restoreListState();
    }
  }, [location, restoreListState]);

  // Outside-click to dismiss dropdown
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setCallSheetOpen(false);
      }
    };
    if (callSheetOpen) document.addEventListener('mousedown', handler);
    else setSmsPhaseAsk(false); // a closed menu forgets a half-made phase pick
    return () => document.removeEventListener('mousedown', handler);
  }, [callSheetOpen]);

  // Auto-dismiss toast. A toast carrying a list of names cannot be read in
  // three seconds, so it gets longer — the names are the point of it.
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), toast.detail?.length ? 9000 : 3000);
    return () => clearTimeout(t);
  }, [toast]);

  const showToast = useCallback(
    (type: 'success' | 'warning' | 'error', msg: string, detail?: string[]) => {
      setToast({ type, msg, detail });
    },
    [],
  );

  /*
   * Reporting a call-sheet send. The DECISION — which tone, which words, which
   * names — lives in `lib/call-sheet-send-result`, pure and exhaustively
   * tested there, because the axis that matters is the TONE and a 1500-line
   * screen is the wrong place to pin it. See that module's header for why.
   */
  const applyReport = useCallback((r: SendReport) => {
    showToast(r.tone, r.msg, r.detail);
  }, [showToast]);

  // ─── Call Sheet actions ─────────────────────────────────────────────────────
  //
  // Crews print call sheets. All three PDF actions go through `lib/blob-tab`,
  // the same helpers as the ticket document menu — see that module for the
  // measurements. Before this, on Smith J-0070 (2026-09-27):
  //
  //   View   opened its tab AFTER the ~2.4s render, inside Chrome's 5s
  //          activation budget only on a fast connection (fe#763's defect).
  //   Print  printed a display-none frame on a load that fired in 27ms, before
  //          any PDF could be in it, never removed it, and had no phone/tablet
  //          path at all.
  //   Save   revoked the blob URL on the line after a.click().

  // A blob wrapped as a PDF: the call-sheet endpoint's bytes arrive untyped.
  const fetchCallSheetBlob = useCallback(async () => {
    const res = await api.jobs.getCallSheetPDF(jobId!);
    return new Blob([res.data], { type: 'application/pdf' });
  }, [jobId]);

  /** The tab is claimed on the click, before the await — never after it. */
  const handleViewPDF = useCallback(async () => {
    if (!jobId) return;
    const win = claimBlobTab();
    if (!win) {
      showToast('error', POPUP_BLOCKED_MESSAGE);
      setCallSheetOpen(false);
      return;
    }
    setActionLoading('view');
    try {
      showBlobInTab(win, await fetchCallSheetBlob());
    } catch {
      // Claimed before the request; do not leave it parked on about:blank.
      win.close();
      showToast('error', 'Failed to load PDF');
    }
    setActionLoading(null);
    setCallSheetOpen(false);
  }, [jobId, showToast, fetchCallSheetBlob]);

  /**
   * Desktop prints through an unseen frame that waits for load and outlives
   * the dialog. A phone or tablet has no inline PDF viewer to print from, so
   * there the PDF opens in a tab claimed inside the click, and the toast says
   * where the print button is now.
   */
  const handlePrint = useCallback(async () => {
    if (!jobId) return;
    const win = printsViaTab() ? claimBlobTab() : null;
    if (printsViaTab() && !win) {
      showToast('error', POPUP_BLOCKED_MESSAGE);
      setCallSheetOpen(false);
      return;
    }
    setActionLoading('print');
    let blob: Blob | null = null;
    try {
      blob = await fetchCallSheetBlob();
    } catch {
      win?.close();
      showToast('error', 'Failed to load PDF for printing');
    }
    if (blob && win) {
      showBlobInTab(win, blob);
      showToast('success', PRINT_IN_TAB_MESSAGE);
    } else if (blob) {
      printBlobInFrame(blob).catch(() => showToast('error', PRINT_FAILED_MESSAGE));
    }
    setActionLoading(null);
    setCallSheetOpen(false);
  }, [jobId, showToast, fetchCallSheetBlob]);

  const handleSavePDF = useCallback(async () => {
    if (!jobId) return;
    setActionLoading('save');
    try {
      saveBlobAs(await fetchCallSheetBlob(), `call-sheet-${jobId}.pdf`);
    } catch { showToast('error', 'Failed to download PDF'); }
    setActionLoading(null);
    setCallSheetOpen(false);
  }, [jobId, showToast, fetchCallSheetBlob]);

  // The print frame outlives the click on purpose (its dialog lives in it);
  // leaving the screen is when it goes.
  useEffect(() => () => disposePrintFrame(), []);

  const handleEmailCrew = useCallback(async () => {
    if (!jobId) return;
    setActionLoading('email');
    try {
      const res = await api.jobs.emailCallSheet(jobId);
      applyReport(describeCallSheetSend(res.data.data, EMAIL_WORDING));
    } catch (err) {
      applyReport(describeCallSheetFailure(err, 'Failed to email call sheet', EMAIL_WORDING));
    }
    setActionLoading(null);
    setCallSheetOpen(false);
  }, [jobId, applyReport]);

  /**
   * Text the call sheet for ONE phase. The API texts that phase's hands plus
   * the both/unstated ones, so a wash text never reaches a run-only hand.
   * Which phase is decided by `onTextToCrew` below.
   */
  const handleSMSCrew = useCallback(async (phase: FieldPhase) => {
    if (!jobId) return;
    setSmsPhaseAsk(false);
    setActionLoading('sms');
    try {
      const res = await api.jobs.smsCallSheet(jobId, phase);
      applyReport(describeCallSheetSend(res.data.data, SMS_WORDING));
    } catch (err) {
      applyReport(describeCallSheetFailure(err, 'Failed to text call sheet', SMS_WORDING));
    }
    setActionLoading(null);
    setCallSheetOpen(false);
  }, [jobId, applyReport]);

  const handleCopyLink = useCallback(() => {
    if (!jobId) return;
    navigator.clipboard.writeText(`${window.location.origin}/jobs/${jobId}`);
    showToast('success', 'Link copied to clipboard');
    setCallSheetOpen(false);
  }, [jobId, showToast]);

  // ─── Data fetching ────────────────────────────────────────────────────────
  const queryClient = useQueryClient();
  const [newTaskTitle, setNewTaskTitle] = useState('');

  const deleteMutation = useMutation({
    mutationFn: () => api.jobs.delete(jobId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      // `['dispatch-jobs']` is not a descendant of `['jobs']`, so without this
      // a deleted job stayed on the dispatch board and calendar for up to the
      // 30s staleTime (App.tsx:250). statusMutation twenty lines below already
      // invalidates this key; delete was the one that did not.
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
      navigate('/jobs', { replace: true });
    },
    onError: () => showToast('error', 'Failed to delete job'),
  });

  const statusMutation = useMutation({
    mutationFn: (newStatus: string) => api.dispatch.updateJob(jobId!, { status: newStatus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-cert-summary'] });
      queryClient.invalidateQueries({ queryKey: ['home-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['field-my-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment-report'] });
      queryClient.invalidateQueries({ queryKey: ['available-equipment', jobId] });
      // A job's status moves its equipment's, which the re-check reads.
      queryClient.invalidateQueries({ queryKey: MAINTENANCE_FLAGS_ROOT });
    },
    onError: () => showToast('error', 'Failed to update status'),
  });

  const resumePhaseMutation = useResumePhaseMutation();

  const { data: job, isLoading, isError } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => api.jobs.get(jobId!).then(r => r.data.data),
    enabled: !!jobId,
  });

  // Fetch tasks
  const { data: tasks } = useQuery({
    queryKey: ['job-tasks', jobId],
    queryFn: () => api.dispatch.getTasks(jobId!).then(r => r.data.data ?? []),
    enabled: !!jobId && !isSales,
  });

  const createTaskMutation = useMutation({
    mutationFn: (title: string) => api.dispatch.createTask(jobId!, { title }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-tasks', jobId] });
      setNewTaskTitle('');
    },
    onError: (err: any) => {
      console.error('[create-task] failed', { jobId, status: err?.response?.status, error: err?.response?.data?.error, message: err?.message });
      const msg = err?.response?.data?.error || err?.message || 'Failed to add task';
      setToast({ type: 'error', msg });
      setTimeout(() => setToast(null), 4000);
    },
  });

  const toggleTaskMutation = useMutation({
    mutationFn: ({ taskId, isCompleted }: { taskId: string; isCompleted: boolean }) =>
      api.dispatch.updateTask(jobId!, taskId, { isCompleted }),
    onMutate: async ({ taskId, isCompleted }) => {
      await queryClient.cancelQueries({ queryKey: ['job-tasks', jobId] });
      const previous = queryClient.getQueryData(['job-tasks', jobId]);
      // eslint-disable-next-line no-restricted-syntax -- TODO PR 2: migrate to setQueryDataMapped (see docs/audits/setquerydata-audit.md)
      queryClient.setQueryData(['job-tasks', jobId], (old: any) =>
        old ? (old as any[]).map((t: any) => t.id === taskId ? { ...t, isCompleted, is_completed: isCompleted } : t) : []
      );
      return { previous };
    },
    onError: (err: any, _vars, context) => {
      // eslint-disable-next-line no-restricted-syntax -- TODO PR 2: migrate to setQueryDataMapped (see docs/audits/setquerydata-audit.md)
      if (context?.previous) queryClient.setQueryData(['job-tasks', jobId], context.previous);
      const msg = err?.response?.data?.error || err?.message || 'Failed to update task';
      setToast({ type: 'error', msg });
      setTimeout(() => setToast(null), 4000);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['job-tasks', jobId] });
    },
  });

  const deleteTaskMutation = useMutation({
    mutationFn: (taskId: string) => api.dispatch.deleteTask(jobId!, taskId),
    onMutate: async (taskId) => {
      await queryClient.cancelQueries({ queryKey: ['job-tasks', jobId] });
      const previous = queryClient.getQueryData(['job-tasks', jobId]);
      // eslint-disable-next-line no-restricted-syntax -- TODO PR 2: migrate to setQueryDataMapped (see docs/audits/setquerydata-audit.md)
      queryClient.setQueryData(['job-tasks', jobId], (old: any) =>
        old ? (old as any[]).filter((t: any) => t.id !== taskId) : []
      );
      return { previous };
    },
    onError: (_err, _taskId, context) => {
      // eslint-disable-next-line no-restricted-syntax -- TODO PR 2: migrate to setQueryDataMapped (see docs/audits/setquerydata-audit.md)
      if (context?.previous) queryClient.setQueryData(['job-tasks', jobId], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['job-tasks', jobId] });
    },
  });

  // ─── Attachments ──────────────────────────────────────────────────────────
  const { data: attachments } = useQuery({
    queryKey: ['job-attachments', jobId],
    queryFn: () => api.jobs.listAttachments(jobId!).then(r => r.data.data ?? []),
    enabled: !!jobId && !isSales,
  });

  const uploadAttachmentMutation = useMutation({
    mutationFn: (file: File) => api.jobs.uploadAttachment(jobId!, file, setUploadProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-attachments', jobId] });
      setUploadProgress(null);
      setAttachmentError(null);
      showToast('success', 'File uploaded');
    },
    onError: (err: any) => {
      setUploadProgress(null);
      const msg = err?.response?.data?.error || err?.message || 'Upload failed';
      setAttachmentError(msg);
    },
  });

  const deleteAttachmentMutation = useMutation({
    mutationFn: (attachmentId: string) => api.jobs.deleteAttachment(jobId!, attachmentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-attachments', jobId] });
      setDeleteAttachmentId(null);
      showToast('success', 'Attachment deleted');
    },
    onError: () => {
      setDeleteAttachmentId(null);
      showToast('error', 'Failed to delete attachment');
    },
  });

  const addLinkMutation = useMutation({
    mutationFn: (data: { label: string; url: string }) => api.jobs.addLinkAttachment(jobId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-attachments', jobId] });
      setShowAddLink(false);
      setLinkLabel('');
      setLinkUrl('');
      setLinkError(null);
      showToast('success', 'Link added');
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || 'Failed to add link';
      setLinkError(msg);
    },
  });

  const handleAddLink = useCallback(() => {
    const trimmedLabel = linkLabel.trim();
    const trimmedUrl = linkUrl.trim();
    if (!trimmedLabel) {
      setLinkError('Label is required');
      return;
    }
    try {
      const parsed = new URL(trimmedUrl);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        setLinkError('URL must start with http:// or https://');
        return;
      }
    } catch {
      setLinkError('Please enter a valid URL (e.g. https://drive.google.com/...)');
      return;
    }
    setLinkError(null);
    addLinkMutation.mutate({ label: trimmedLabel, url: trimmedUrl });
  }, [linkLabel, linkUrl, addLinkMutation]);

  const handleAttachmentSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    if (!file) return;
    const error = validateAttachmentFile(file);
    if (error) {
      setAttachmentError(error);
      return;
    }
    setAttachmentError(null);
    uploadAttachmentMutation.mutate(file);
  }, [uploadAttachmentMutation]);

  const cameFromDispatch = location.state?.from?.includes('/dispatch');

  // ─── Loading / Error states ───────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <BackHeader to={cameFromDispatch ? '/dispatch' : '/jobs'} label={cameFromDispatch ? 'Dispatch' : 'Jobs'} preserveState />
        <div className="flex justify-center py-12">
          <Loader size={32} className="animate-spin text-fieldiq-accent" />
        </div>
      </div>
    );
  }

  if (isError || !job) {
    return (
      <div className="min-h-full bg-fieldiq-dark">
        <BackHeader to={cameFromDispatch ? '/dispatch' : '/jobs'} label={cameFromDispatch ? 'Dispatch' : 'Jobs'} preserveState />
        <div className="max-w-[1920px] mx-auto px-4 py-8">
          <div className="bg-red-500/10 border border-red-500 rounded-xl p-4 text-red-400 text-sm">
            Failed to load job details.
          </div>
        </div>
      </div>
    );
  }

  // ─── Data extraction ──────────────────────────────────────────────────────
  const status = job.status || 'pending';
  const crew: any[] = job.crew ?? [];
  const equipment: any[] = job.equipment ?? [];
  const companyTax = job.companyTax ?? job.company_tax;
  const ticketCount: number = job.ticketCount ?? job.ticket_count ?? 0;
  // Tolerates both the legacy bare-string entries and the { id, name }
  // objects the API is moving the column to — see lib/service-entries.ts.
  // Rendering the raw array put an object in front of React as a child.
  const services: string[] = serviceNames(job.servicesRequired ?? job.services_required);
  const customerName = job.customerName ?? job.customer_name ?? '';
  const customerContactName = job.customerContactName ?? job.customer_contact_name ?? '';
  const customerContactPhone = job.customerContactPhone ?? job.customer_contact_phone ?? '';
  const country = companyTax?.country ?? 'CA';

  const depthM = job.depthMetres ?? job.depth_metres;
  const rigName = job.rigName ?? job.rig_name;
  const rigNumber = job.rigNumber ?? job.rig_number;
  const wellName = job.wellName ?? job.well_name;
  const wellIdLsd = job.wellIdLsd ?? job.well_id_lsd;
  const wellIdApi = job.wellIdApi ?? job.well_id_api;
  const pressureKpa = job.pressureKpa ?? job.pressure_kpa;
  const jobType = job.jobType ?? job.job_type;
  const scheduledDate = job.scheduledDate ?? job.scheduled_date;
  const washDate = job.dateTimeWash ?? job.date_time_wash;
  const locationCity = job.locationCity ?? job.location_city;
  const locationProv = job.locationProvinceState ?? job.location_province_state;
  const afeNumber = job.afeNumber ?? job.afe_number;
  const wellLicence = job.wellLicence ?? job.well_licence;
  const directions = job.directions;
  const specialInstructions = job.specialInstructions ?? job.special_instructions;
  const jobNumber = job.jobNumber ?? job.job_number;
  const formation = job.formation;

  const depthLine = [formation, depthM ? `${Number(depthM).toLocaleString()}m` : null].filter(Boolean).join(' \u00B7 ');

  // Every string on the job, one card each — see lib/casing-strings-display.
  const casingStrings = jobCasingStringsForDisplay(job);

  // Torque profile is loaded via hook above (before early returns)


  return (
    <div className="min-h-full bg-fieldiq-dark pb-44 md:pb-0">
      {/* ─── Header ──────────────────────────────────────────────────────── */}
      <BackHeader
        to={cameFromDispatch ? '/dispatch' : '/jobs'}
        label={cameFromDispatch ? 'Dispatch' : 'Back'}
        preserveState
      />

      {/* Job number + status + flag bar */}
      <div className="max-w-[1920px] mx-auto px-4 pt-2 pb-1 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-white text-lg font-bold">
            {jobNumber}
            {customerName && <span className="text-muted-foreground font-medium"> · {customerName}</span>}
          </h1>
          {/* `status` is `any` off the wire and the shared maps are keyed on the
              enum, hence the cast. Both `||` fallbacks are kept exactly as they
              were so this stays an extraction and not a behaviour change. */}
          <span className={`inline-flex items-center px-3 py-0.5 rounded-full text-xs font-semibold ${JOB_STATUS_STYLES[status as JobStatus] || JOB_STATUS_STYLES.pending}`}>
            {JOB_STATUS_LABELS[status as JobStatus] || status}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {/* Status action buttons — none of these is drawn for sales. */}
          {!isSales && (<>
          {(status === 'pending' || status === 'scheduled') && (
            <button
              onClick={() => statusMutation.mutate('active')}
              disabled={statusMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-500/20 text-green-400 text-sm font-semibold hover:bg-green-500/30 transition-colors disabled:opacity-50"
            >
              <Play className="w-4 h-4" />
              Start Job
            </button>
          )}
          {status === 'active' && (
            <button
              onClick={() => statusMutation.mutate('completed')}
              disabled={statusMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/20 text-emerald-400 text-sm font-semibold hover:bg-emerald-500/30 transition-colors disabled:opacity-50"
            >
              <CheckCircle2 className="w-4 h-4" />
              Complete
            </button>
          )}
          {/* Resume an ENDED wash or run (API 0534): the crew is back after a
              gap. A refusal toasts the API's own sentence. */}
          {(['wash', 'run'] as const).filter((p) => phaseIsResumable(job, p)).map((p) => (
            <button
              key={`resume-${p}`}
              onClick={() => resumePhaseMutation.mutate({ jobId: jobId!, phase: p, jobNumber })}
              disabled={resumePhaseMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-fieldiq-card border border-fieldiq-border text-fieldiq-text text-sm font-semibold hover:bg-fieldiq-border/30 transition-colors disabled:opacity-50"
            >
              <RotateCw className="w-4 h-4" />
              {p === 'wash' ? 'Resume wash' : 'Resume run'}
            </button>
          ))}
          <button
            onClick={() => setChatOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-fieldiq-accent text-fieldiq-accent text-sm font-semibold hover:bg-fieldiq-accent/10 transition-colors"
          >
            <MessageSquare className="w-4 h-4" />
            Chat
          </button>
          <button
            onClick={() => navigate(`/jobs/${jobId}/edit`)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-fieldiq-border text-fieldiq-text text-sm font-medium hover:bg-fieldiq-border/30 transition-colors"
          >
            <Pencil className="w-4 h-4" />
            Edit
          </button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center justify-center w-9 h-9 rounded-lg border border-fieldiq-border text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
          </>)}
          <CountryFlag country={country} />
        </div>
      </div>

      <main className="max-w-[1920px] mx-auto px-4 py-3 pb-4 space-y-4">
        {/* ─── Customer Info Card ────────────────────────────────────────── */}
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5">
          {customerName && <h2 className="text-white text-lg font-bold">{customerName}</h2>}
          {(customerContactName || customerContactPhone) && (
            <div className="flex items-center gap-3 mt-1">
              {customerContactName && (
                <span className="text-fieldiq-text text-sm flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-fieldiq-muted" />
                  {customerContactName}
                </span>
              )}
              {customerContactPhone && (
                <a
                  href={`tel:${customerContactPhone}`}
                  className="text-fieldiq-accent text-sm flex items-center gap-1.5 hover:text-amber-400 transition-colors"
                >
                  <Phone className="w-3.5 h-3.5" />
                  {customerContactPhone}
                </a>
              )}
            </div>
          )}
          {wellName && <p className="text-fieldiq-muted text-sm mt-1">{wellName}</p>}

          <div className="grid grid-cols-2 gap-x-8 gap-y-4 mt-5">
            {wellIdLsd && <InfoField icon={FileText} label={wellIdLabel} value={wellIdLsd} />}
            {(wellIdApi && !wellIdLsd) && <InfoField icon={FileText} label={wellIdLabel} value={wellIdApi} />}
            {rigName && (
              <InfoField icon={Wrench} label="Rig" value={`${rigName}${rigNumber ? ` (${rigNumber})` : ''}`} />
            )}
            {depthM && <InfoField icon={Tag} label="Depth" value={formatDepth(Math.round(toDisplayDepth(Number(depthM))))} />}
            {pressureKpa && <InfoField icon={Gauge} label="Pressure" value={formatPressure(Math.round(toDisplayPressure(Number(pressureKpa))))} />}
            {jobType && <InfoField icon={Layers} label="Job Type" value={getJobTypeLabel(jobType)} />}
            {scheduledDate && (
              <InfoField icon={Calendar} label="Scheduled" value={formatCalendarDate(scheduledDate, { year: 'numeric', month: 'short', day: 'numeric' }, 'en-CA') ?? '—'} />
            )}
            {/*
              Rendered whenever there IS a scheduled day, so an absent time
              reads as an em dash rather than as a missing row. "No time set" is
              a real answer a crew needs — the row vanishing is indistinguishable
              from the screen not having the field at all.

              "On Location" is the operator's name for this, and it is the SAME
              name everywhere scheduled_date's time renders. It was briefly
              "Scheduled Time" here to match NewJobScreen's picker, which gave
              one column two names — and two names for one column is how a
              future reader concludes they are two fields.

              "Wash Time" below is genuinely a different column (date_time_wash)
              and keeps its own name.

              NewJobScreen's PICKER for this column carries the same name, so
              the operator types into "On Location" and reads it back as
              "On Location". Write side and read side agree; there is no second
              name for this column left in the app.
            */}
            {scheduledDate && (
              <InfoField icon={Clock} label="On Location" value={formatScheduledTime(scheduledDate) ?? '—'} />
            )}
            {washDate && (
              <InfoField icon={Calendar} label="Wash Date" value={formatCalendarDate(washDate, { year: 'numeric', month: 'short', day: 'numeric' }, 'en-CA') ?? '—'} />
            )}
            {washDate && (
              <InfoField icon={Clock} label="Wash Time" value={formatScheduledTime(washDate) ?? '—'} />
            )}
            {(locationCity || locationProv) && (
              <InfoField icon={MapPin} label="Location" value={[locationCity, locationProv].filter(Boolean).join(', ')} />
            )}
            {afeNumber && <InfoField icon={Hash} label="AFE" value={afeNumber} />}
            {wellLicence && <InfoField icon={FileText} label="Well Licence" value={wellLicence} />}
          </div>
        </div>

        {/* ─── Services Required ─────────────────────────────────────────── */}
        {services.length > 0 && (
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5">
            <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-fieldiq-accent" />
              Services Required
            </h3>
            <div className="flex flex-wrap gap-2">
              {services.map((svc, i) => (
                <span
                  key={svc}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${SERVICE_COLORS[i % SERVICE_COLORS.length]}`}
                >
                  <CheckCircle2 className="w-3 h-3" />
                  {svc}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* ─── Casing Specs — one card per string ─────────────────────────
            Was two cards built from the flat string-1 and string-2 columns, so a
            third string never showed. Same card as the field screen, read-only
            here: the office changes torque on the job form. The heading suffix
            is the string_number, never the array index. */}
        {casingStrings.map((s, i) => (
          <FieldCasingStringCard
            key={s.id ?? `string-${s.string_number}`}
            string={s}
            headingSuffix={casingStrings.length > 1 ? `String ${s.string_number}` : null}
            emptyTorqueHint="No torque profile found. Edit the job to add torque values."
            note={i === 0 && depthLine ? <p className="text-fieldiq-muted text-sm">{depthLine}</p> : undefined}
          />
        ))}

        {/* ─── Directions (collapsible) ──────────────────────────────────── */}
        {directions && (
          <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border">
            <button
              onClick={() => setDirectionsOpen(!directionsOpen)}
              className="w-full flex items-center justify-between p-5"
            >
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Navigation className="w-4 h-4 text-fieldiq-accent" />
                Directions
              </h3>
              <ChevronDown className={`w-5 h-5 text-fieldiq-muted transition-transform ${directionsOpen ? 'rotate-180' : ''}`} />
            </button>
            {!directionsOpen && (
              <p className="text-fieldiq-muted text-sm px-5 pb-4 -mt-2 truncate">{directions}</p>
            )}
            {directionsOpen && (
              <p className="text-fieldiq-text text-sm px-5 pb-5 whitespace-pre-wrap">{directions}</p>
            )}
          </div>
        )}

        {/* ─── Special Instructions (highlighted) ────────────────────────── */}
        {specialInstructions && (
          <div className="bg-amber-500/10 rounded-xl border border-amber-500/30 p-5">
            <h3 className="text-white font-semibold mb-2 flex items-center gap-2">
              <FileText className="w-4 h-4 text-amber-400" />
              Special Instructions
            </h3>
            <p className="text-fieldiq-text text-sm whitespace-pre-wrap">{specialInstructions}</p>
          </div>
        )}

        {/* ─── Crew ──────────────────────────────────────────────────────── */}
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Users className="w-4 h-4 text-fieldiq-accent" />
              Crew ({crew.length})
            </h3>
            {!isSales && status !== 'completed' && status !== 'cancelled' && (
              <button
                onClick={() => navigate(`/jobs/${jobId}/assign-crew`)}
                className="flex items-center gap-1 text-fieldiq-accent text-xs font-semibold hover:text-amber-400 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Assign
              </button>
            )}
          </div>
          {crew.length > 0 ? (() => {
            // Group crew by their position
            const grouped: Record<string, any[]> = {};
            crew.forEach((member: any) => {
              const groupLabel = crewRoleLabel(member);
              if (!grouped[groupLabel]) grouped[groupLabel] = [];
              grouped[groupLabel].push(member);
            });

            return (
              <div className="space-y-5">
                {Object.entries(grouped).map(([role, members]) => (
                  <div key={role}>
                    <h4 className="text-fieldiq-accent text-xs font-bold uppercase tracking-wider mb-2 ml-1">{role.replace(/_/g, ' ')}</h4>
                    <div className="space-y-2">
                      {members.map((member: any) => {
                        const initials = member.initials || `${(member.first_name?.[0] || '').toUpperCase()}${(member.last_name?.[0] || '').toUpperCase()}`;
                        const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ');
                        const certs: any[] = member.certifications ?? [];
                        return (
                          <div key={member.id} className="flex items-start gap-3 bg-fieldiq-dark rounded-xl px-4 py-3 border border-fieldiq-border">
                            <span className="w-10 h-10 bg-fieldiq-accent/20 text-fieldiq-accent rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5">
                              {initials}
                            </span>
                            <div className="min-w-0 flex-1">
                              <p className="text-white text-sm font-semibold">{fullName}</p>
                              {certs.length > 0 && (
                                <div className="flex flex-wrap gap-1.5 mt-2">
                                  {certs.map((cert: any, ci: number) => {
                                    const style = CERT_STYLES[cert.type] || CERT_STYLES.other;
                                    const isExpiring = cert.status === 'expiring_soon';
                                    return (
                                      <span
                                        key={ci}
                                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${style.color}`}
                                      >
                                        <CheckCircle2 className="w-2.5 h-2.5" />
                                        {style.label}
                                        {isExpiring && <AlertTriangle className="w-2.5 h-2.5 text-yellow-400" />}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            );
          })() : (
            <p className="text-fieldiq-muted text-sm">No crew assigned</p>
          )}
        </div>

        {/* ─── Equipment ─────────────────────────────────────────────────── */}
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Wrench className="w-4 h-4 text-fieldiq-accent" />
              Equipment
            </h3>
            {!isSales && status !== 'completed' && status !== 'cancelled' && (
              <button
                onClick={() => navigate(`/jobs/${jobId}/assign-equipment`)}
                className="flex items-center gap-1 text-fieldiq-accent text-xs font-semibold hover:text-amber-400 transition-colors"
              >
                <Plus className="w-3.5 h-3.5" />
                Assign
              </button>
            )}
          </div>
          {equipment.length > 0 ? (
            <div className="divide-y divide-fieldiq-border">
              {equipment.map((item: any) => {
                const eqStatus = item.status || 'available';
                const statusLabel = EQUIP_STATUS_LABELS[eqStatus] || eqStatus;
                const statusColor = EQUIP_STATUS_STYLES[eqStatus] || 'text-gray-400';
                const needsPickup = eqStatus === 'left_on_site';
                return (
                  <div key={item.id} className="flex items-start justify-between py-3">
                    <div>
                      <p className="text-white text-sm font-medium">{item.name}</p>
                      <p className="text-fieldiq-muted text-xs">{item.type}</p>
                      {needsPickup && (
                        <p className="text-orange-400 text-xs mt-1 flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Left on Site — Needs Pickup!
                        </p>
                      )}
                    </div>
                    <span className={`text-xs font-medium whitespace-nowrap ${statusColor}`}>
                      {statusLabel}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-fieldiq-muted text-sm">No equipment assigned</p>
          )}
        </div>

        {/* This job's maintenance checks (api#944): the job endpoint returns only
            flags carrying this job's id. Renders nothing outside suggest mode. */}
        {jobId && <MaintenanceFlagsPanel subject={{ kind: 'job', id: jobId }} />}

        {/* ─── Tasks ──────────────────────────────────────────────────────── */}
        {!isSales && (() => {
          const taskList: any[] = tasks ?? [];
          const completedTasks = taskList.filter((t: any) => t.is_completed || t.isCompleted).length;
          return (
            <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5">
              <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-fieldiq-accent" />
                Tasks ({completedTasks}/{taskList.length})
              </h3>
              {taskList.length > 0 && (
                <div className="h-2 bg-fieldiq-border rounded-full overflow-hidden mb-4">
                  <div
                    className="h-full bg-fieldiq-accent-signal rounded-full transition-all"
                    style={{ width: `${(completedTasks / taskList.length) * 100}%` }}
                  />
                </div>
              )}
              {taskList.length > 0 && (
                <div className="space-y-2 mb-4">
                  {taskList.map((task: any) => {
                    const done = task.is_completed || task.isCompleted;
                    return (
                      <div key={task.id} className="flex items-center gap-3 py-2.5 px-3 rounded-lg bg-fieldiq-dark border border-fieldiq-border group">
                        <button
                          onClick={() => toggleTaskMutation.mutate({ taskId: task.id, isCompleted: !done })}
                          className="flex-shrink-0"
                        >
                          {done ? (
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                          ) : (
                            <Circle className="w-4 h-4 text-fieldiq-muted hover:text-fieldiq-accent transition-colors" />
                          )}
                        </button>
                        <span className={`text-sm flex-1 ${done ? 'text-fieldiq-muted line-through' : 'text-white'}`}>
                          {task.title}
                        </span>
                        <button
                          onClick={() => deleteTaskMutation.mutate(task.id)}
                          className="flex-shrink-0 opacity-0 group-hover:opacity-100 text-fieldiq-muted hover:text-red-400 transition-all"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              {taskList.length === 0 && (
                <p className="text-fieldiq-muted text-sm mb-4">No tasks yet</p>
              )}
              {status !== 'completed' && status !== 'cancelled' && (
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newTaskTitle.trim()) {
                        createTaskMutation.mutate(newTaskTitle.trim());
                      }
                    }}
                    placeholder="Add a task..."
                    className="flex-1 bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-sm text-white placeholder:text-fieldiq-muted focus:outline-none focus:border-amber-500/50 transition-colors"
                  />
                  <button
                    onClick={() => { if (newTaskTitle.trim()) createTaskMutation.mutate(newTaskTitle.trim()); }}
                    disabled={!newTaskTitle.trim() || createTaskMutation.isPending}
                    className="flex items-center gap-1.5 px-3 py-2 bg-fieldiq-accent text-black text-xs font-semibold rounded-lg hover:bg-amber-400 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {createTaskMutation.isPending ? <Loader className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
                    Add
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* ─── Attachments ───────────────────────────────────────────────── */}
        {!isSales && (
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-fieldiq-accent" />
              Attachments{attachments && attachments.length > 0 ? ` (${attachments.length})` : ''}
            </h3>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowAddLink(true)}
                className="flex items-center gap-1 text-fieldiq-accent text-xs font-semibold hover:text-amber-400 transition-colors"
              >
                <Link2 className="w-3.5 h-3.5" />
                Add Link
              </button>
              <input
                ref={attachmentInputRef}
                type="file"
                accept={ALLOWED_ATTACHMENT_TYPES}
                onChange={handleAttachmentSelect}
                className="hidden"
              />
              <button
                onClick={() => attachmentInputRef.current?.click()}
                disabled={uploadAttachmentMutation.isPending}
                className="flex items-center gap-1 text-fieldiq-accent text-xs font-semibold hover:text-amber-400 transition-colors disabled:opacity-50"
              >
                <Upload className="w-3.5 h-3.5" />
                Upload
              </button>
            </div>
          </div>

          {/* Upload progress */}
          {uploadProgress !== null && (
            <div className="mb-4">
              <div className="h-2 bg-fieldiq-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-fieldiq-accent-signal rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-fieldiq-muted mt-1">Uploading… {uploadProgress}%</p>
            </div>
          )}

          {/* Validation / upload error */}
          {attachmentError && (
            <div className="mb-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm flex-1">{attachmentError}</p>
              <button onClick={() => setAttachmentError(null)} className="text-red-400 hover:text-red-300">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {/* Attachment list */}
          {attachments && attachments.length > 0 ? (
            <div className="divide-y divide-fieldiq-border">
              {attachments.map((att: any) => {
                const isLink = att.type === 'external_link' || att.attachment_type === 'external_link';
                const displayName = isLink
                  ? (att.label ?? att.original_filename ?? att.originalFilename)
                  : (att.original_filename ?? att.originalFilename);
                const href = isLink
                  ? (att.external_url ?? att.externalUrl ?? att.url)
                  : (att.download_url ?? att.downloadUrl);
                return (
                  <div key={att.id} className="flex items-center gap-3 py-3">
                    <FileText className="w-4 h-4 text-fieldiq-muted flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-medium truncate">{displayName}</p>
                      <p className="text-fieldiq-muted text-xs">
                        {isLink ? 'Link' : formatFileSize(att.size_bytes)}
                        {(att.uploaded_by_name ?? att.uploadedByName) && ` · ${att.uploaded_by_name ?? att.uploadedByName}`}
                        {(att.created_at ?? att.createdAt) && ` · ${formatInstantDay(att.created_at ?? att.createdAt, timeZone, { year: 'numeric', month: 'short', day: 'numeric' }, 'en-CA') ?? '—'}`}
                      </p>
                    </div>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-fieldiq-accent text-xs font-semibold hover:text-amber-400 transition-colors flex-shrink-0"
                    >
                      <Download className="w-3.5 h-3.5" />
                      {isLink ? 'Open' : 'Download'}
                    </a>
                    <button
                      onClick={() => setDeleteAttachmentId(att.id)}
                      className="text-fieldiq-muted hover:text-red-400 transition-colors flex-shrink-0"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-fieldiq-muted text-sm">No attachments</p>
          )}
        </div>
        )}

        {/* ─── Timestamps ────────────────────────────────────────────────── */}
        <div className="bg-fieldiq-card rounded-xl border border-fieldiq-border p-4">
          <div className="flex items-center gap-4 text-xs text-fieldiq-muted">
            {job.created_at && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Created {formatInstantDay(job.created_at, timeZone) ?? '—'}
              </span>
            )}
            {job.updated_at && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Updated {formatInstantDay(job.updated_at, timeZone) ?? '—'}
              </span>
            )}
          </div>
        </div>

      </main>

      {/* ─── Toast ───────────────────────────────────────────────────────── */}
      {toast && (
        /*
         * THREE tones, not two. A partial send is neither a success nor a
         * failure and must not borrow the styling of either: green with a
         * lower count reads as "done", which is how a dispatcher comes to
         * believe a crew was called out when a hand was skipped. Amber is the
         * whole point of this component's change.
         */
        <div
          role="status"
          aria-live="polite"
          className={`fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg text-sm font-medium shadow-lg transition-all max-w-[92vw] ${
            toast.type === 'success'
              ? 'bg-green-600 text-white'
              : toast.type === 'warning'
                ? 'bg-fieldiq-accent text-fieldiq-dark'
                : 'bg-red-600 text-white'
          }`}
        >
          <div>{toast.msg}</div>
          {!!toast.detail?.length && (
            <ul className="mt-1 space-y-0.5 text-xs font-normal opacity-90 list-disc list-inside text-left">
              {toast.detail.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ─── Bottom Action Bar ────────────── */}
      <div className="bg-fieldiq-card border-t border-fieldiq-border px-4 py-3">
        <div className={`max-w-[1920px] mx-auto grid ${isSales ? 'grid-cols-1' : 'grid-cols-3'} gap-3`}>
          {/* Call Sheet dropdown and Create Ticket — not drawn for sales. */}
          {!isSales && (<>
          {/* Call Sheet dropdown */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setCallSheetOpen(!callSheetOpen)}
              className="w-full flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl border border-border bg-secondary text-foreground text-sm font-medium hover:bg-secondary/80 transition-colors"
            >
              <FileText className="w-5 h-5" />
              <span className="flex items-center gap-1">
                Call Sheet
                <ChevronUp className={`w-3 h-3 transition-transform ${callSheetOpen ? '' : 'rotate-180'}`} />
              </span>
            </button>

            {callSheetOpen && (
              <div className="absolute bottom-full left-0 mb-2 w-56 bg-fieldiq-card border border-fieldiq-border rounded-xl shadow-xl overflow-hidden z-50">
                <DropdownItem icon={Eye} label="View PDF" loading={actionLoading === 'view'} onClick={handleViewPDF} />
                <DropdownItem icon={Printer} label="Print" loading={actionLoading === 'print'} onClick={handlePrint} />
                <DropdownItem icon={Download} label="Save as PDF" loading={actionLoading === 'save'} onClick={handleSavePDF} />
                <div className="border-t border-fieldiq-border" />
                <DropdownItem icon={Mail} label="Email to Crew" loading={actionLoading === 'email'} onClick={handleEmailCrew} />
                {/*
                  ONE Send. A SPLIT job (wash on a different day from the run)
                  has two call sheets, so the tap asks Wash or Run and texts
                  that phase only — the API requires the phase there
                  (PHASE_REQUIRED). Any other job sends the run at once: with
                  no wash date the run is the only phase, and on a same-day job
                  the API refuses a wash send and its run call sheet is the
                  day's (api#1346 session, 2026-11-03).
                */}
                {smsPhaseAsk && job ? (
                  <div className="px-4 py-2.5 space-y-2" role="group" aria-label="Text which call sheet?">
                    <p className="text-xs text-fieldiq-muted">Text which call sheet?</p>
                    <div className="flex gap-2">
                      {(['wash', 'run'] as const).map((p) => (
                        <button
                          key={p}
                          // The tapped item unmounts; focus moves here rather
                          // than falling to <body>.
                          autoFocus={p === 'wash'}
                          type="button"
                          onClick={() => handleSMSCrew(p)}
                          className="flex-1 px-2 py-1.5 rounded-lg text-sm font-medium bg-fieldiq-dark border border-fieldiq-border text-white hover:border-fieldiq-accent"
                        >
                          {PHASE_WORD[p]} · {formatCalendarDate(phaseDateValue(job, p), { month: 'short', day: 'numeric' }) ?? '—'}
                        </button>
                      ))}
                    </div>
                    <button type="button" onClick={() => setSmsPhaseAsk(false)} className="text-xs text-fieldiq-muted hover:text-white">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <DropdownItem
                    icon={MessageSquare}
                    label="Text to Crew"
                    loading={actionLoading === 'sms'}
                    onClick={() => (job && jobHasSeparateWashDay(job) ? setSmsPhaseAsk(true) : handleSMSCrew('run'))}
                  />
                )}
                <div className="border-t border-fieldiq-border" />
                <DropdownItem icon={Link} label="Copy Link" onClick={handleCopyLink} />
              </div>
            )}
          </div>

          {/* Create Ticket */}
          <button
            onClick={() => navigate(`/tickets/new?jobId=${jobId}`)}
            className="flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl bg-amber-500 text-black text-sm font-semibold hover:bg-amber-400 transition-colors"
          >
            <Receipt className="w-5 h-5" />
            Create Ticket
          </button>
          </>)}

          {/* View Tickets */}
          <button
            onClick={() => navigate(`/tickets?jobId=${jobId}`)}
            className="flex flex-col items-center gap-1 px-3 py-2.5 rounded-xl border border-border bg-secondary text-foreground text-sm font-medium hover:bg-secondary/80 transition-colors"
          >
            <Eye className="w-5 h-5" />
            View Tickets{ticketCount > 0 ? ` (${ticketCount})` : ''}
          </button>
        </div>
      </div>

      {/* ─── Delete Confirmation Modal ───────────────────────────────── */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-bold text-white mb-2">Delete Job</h3>
            <p className="text-sm text-white mb-6">
              Are you sure you want to delete Job {jobNumber}? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="flex-1 py-2.5 bg-fieldiq-dark text-white font-medium rounded-lg text-sm hover:text-white transition-colors"
                disabled={deleteMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate()}
                disabled={deleteMutation.isPending}
                className="flex-1 py-2.5 bg-red-500 text-white font-semibold rounded-lg text-sm hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleteMutation.isPending && (
                  <Loader size={14} className="animate-spin" />
                )}
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Delete Attachment Confirmation Modal ─────────────────── */}
      {deleteAttachmentId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-bold text-white mb-2">Delete Attachment</h3>
            <p className="text-sm text-white mb-6">
              Are you sure you want to delete this attachment? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteAttachmentId(null)}
                className="flex-1 py-2.5 bg-fieldiq-dark text-white font-medium rounded-lg text-sm hover:text-white transition-colors"
                disabled={deleteAttachmentMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={() => deleteAttachmentMutation.mutate(deleteAttachmentId)}
                disabled={deleteAttachmentMutation.isPending}
                className="flex-1 py-2.5 bg-red-500 text-white font-semibold rounded-lg text-sm hover:bg-red-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {deleteAttachmentMutation.isPending && (
                  <Loader size={14} className="animate-spin" />
                )}
                {deleteAttachmentMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Add Link Modal ──────────────────────────────────────── */}
      {showAddLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-bold text-white mb-4">Add Link</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-fieldiq-muted mb-1">Label</label>
                <input
                  type="text"
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  placeholder="e.g. Torque Sheet"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm placeholder:text-fieldiq-muted focus:outline-none focus:border-fieldiq-accent"
                />
              </div>
              <div>
                <label className="block text-sm text-fieldiq-muted mb-1">URL</label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://drive.google.com/..."
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm placeholder:text-fieldiq-muted focus:outline-none focus:border-fieldiq-accent"
                />
              </div>
              {linkError && (
                <p className="text-red-400 text-xs">{linkError}</p>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => { setShowAddLink(false); setLinkLabel(''); setLinkUrl(''); setLinkError(null); }}
                className="flex-1 py-2.5 bg-fieldiq-dark text-white font-medium rounded-lg text-sm hover:text-white transition-colors"
                disabled={addLinkMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleAddLink}
                disabled={addLinkMutation.isPending}
                className="flex-1 py-2.5 bg-fieldiq-accent text-black font-semibold rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {addLinkMutation.isPending && (
                  <Loader size={14} className="animate-spin" />
                )}
                {addLinkMutation.isPending ? 'Adding...' : 'Add Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Crew Chat Drawer */}
      {jobId && (
        <JobChatDrawer
          jobId={jobId}
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          currentUserId={user?.id || ''}
          onNewMessage={(msg) => {
            const name = [msg.user?.firstName, msg.user?.lastName].filter(Boolean).join(' ') || 'Someone';
            showToast('success', `${name}: ${(msg.message || '').slice(0, 50)}`);
          }}
        />
      )}
    </div>
  );
};

// ─── Sub-components ─────────────────────────────────────────────────────────

const InfoField: React.FC<{ icon: any; label: string; value: string }> = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-2.5">
    <Icon className="w-4 h-4 text-fieldiq-muted mt-0.5 flex-shrink-0" />
    <div className="min-w-0">
      <p className="text-[11px] text-fieldiq-muted uppercase tracking-wide">{label}</p>
      <p className="text-sm text-white font-medium truncate">{value}</p>
    </div>
  </div>
);

const DropdownItem: React.FC<{ icon: any; label: string; loading?: boolean; onClick: () => void }> = ({ icon: Icon, label, loading, onClick }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-fieldiq-text hover:bg-fieldiq-border/30 transition-colors disabled:opacity-50"
  >
    {loading ? <Loader className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
    {label}
  </button>
);
